import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { useRuleExecutions } from '@/hooks/useScheduling';
import { AutomationRule, RuleExecution } from '@/services/api/scheduling';
import { cn } from '@/lib/utils';

interface ExecutionHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: AutomationRule | null;
}

export const ExecutionHistoryDrawer: React.FC<ExecutionHistoryDrawerProps> = ({
  open,
  onOpenChange,
  rule
}) => {
  const {
    data: executionsData,
    isLoading,
    error,
    refetch
  } = useRuleExecutions(rule?.id || '', { limit: 50 });

  const executions = executionsData?.executions || [];

  // Get status badge
  const getStatusBadge = (status: RuleExecution['status']) => {
    switch (status) {
      case 'success':
        return (
          <Badge variant='outline' className='bg-green-50 text-green-700 border-green-200'>
            <CheckCircle className='h-3 w-3 mr-1' />
            Success
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant='outline' className='bg-red-50 text-red-700 border-red-200'>
            <XCircle className='h-3 w-3 mr-1' />
            Failed
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant='outline' className='bg-blue-50 text-blue-700 border-blue-200'>
            <Loader2 className='h-3 w-3 mr-1 animate-spin' />
            Pending
          </Badge>
        );
      case 'skipped':
        return (
          <Badge variant='outline' className='bg-gray-50 text-gray-700 border-gray-200'>
            <Clock className='h-3 w-3 mr-1' />
            Skipped
          </Badge>
        );
      default:
        return <Badge variant='outline'>{status}</Badge>;
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format duration
  const formatDuration = (seconds: number | null) => {
    if (seconds === null) return '-';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Get triggered by label
  const getTriggeredByLabel = (triggeredBy: RuleExecution['triggered_by']) => {
    switch (triggeredBy) {
      case 'automation':
        return 'Automated';
      case 'manual':
        return 'Manual';
      case 'schedule':
        return 'Scheduled';
      default:
        return triggeredBy;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='w-[600px] sm:max-w-[600px]'>
        <SheetHeader>
          <SheetTitle>Execution History</SheetTitle>
          <SheetDescription>
            {rule ? `Executions for rule: ${rule.name}` : 'Select a rule to view history'}
          </SheetDescription>
        </SheetHeader>

        <div className='mt-6'>
          {/* Header with refresh button */}
          <div className='flex items-center justify-between mb-4'>
            <div className='text-sm text-muted-foreground'>
              {executions.length} execution{executions.length !== 1 ? 's' : ''} found
              {rule && rule.execution_count > 0 && executions.length !== rule.execution_count && (
                <span className='ml-2 text-amber-600'>
                  (Rule shows {rule.execution_count} total)
                </span>
              )}
            </div>
            <Button variant='outline' size='sm' onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={cn('h-4 w-4 mr-2', isLoading && 'animate-spin')} />
              Refresh
            </Button>
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className='flex items-center justify-center h-64'>
              <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            </div>
          )}

          {/* Error state */}
          {error && !isLoading && (
            <div className='flex flex-col items-center justify-center h-64 gap-4'>
              <AlertCircle className='h-8 w-8 text-destructive' />
              <p className='text-muted-foreground text-center'>
                Failed to load execution history
                <br />
                <span className='text-sm'>
                  {error instanceof Error ? error.message : 'Unknown error'}
                </span>
              </p>
              <Button variant='outline' onClick={() => refetch()}>
                <RefreshCw className='h-4 w-4 mr-2' />
                Retry
              </Button>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && executions.length === 0 && (
            <div className='flex flex-col items-center justify-center h-64 gap-4'>
              <Clock className='h-12 w-12 text-muted-foreground' />
              <div className='text-center'>
                <h4 className='font-medium'>No Executions Yet</h4>
                <p className='text-sm text-muted-foreground'>
                  This rule hasn't been triggered yet.
                </p>
              </div>
            </div>
          )}

          {/* Executions table */}
          {!isLoading && !error && executions.length > 0 && (
            <div className='border rounded-lg overflow-hidden'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date/Time</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Triggered By</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>VIN Job</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {executions.map(execution => (
                    <TableRow key={execution.id}>
                      <TableCell className='font-medium'>
                        {formatDate(execution.executed_at)}
                      </TableCell>
                      <TableCell>{getStatusBadge(execution.status)}</TableCell>
                      <TableCell>
                        <Badge variant='secondary' className='text-xs'>
                          {getTriggeredByLabel(execution.triggered_by)}
                        </Badge>
                      </TableCell>
                      <TableCell className='text-muted-foreground'>
                        {formatDuration(execution.duration_seconds)}
                      </TableCell>
                      <TableCell>
                        {execution.vin_job_id ? (
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-auto p-0 text-primary hover:underline'
                            onClick={() => {
                              // In a real implementation, this would navigate to the VIN job details
                              console.log('View VIN job:', execution.vin_job_id);
                            }}
                          >
                            {execution.vin_job_id.slice(0, 8)}...
                            <ExternalLink className='h-3 w-3 ml-1' />
                          </Button>
                        ) : execution.status === 'failed' && execution.error_message ? (
                          <span
                            className='text-xs text-destructive truncate max-w-[150px] block'
                            title={execution.error_message}
                          >
                            {execution.error_message}
                          </span>
                        ) : (
                          <span className='text-muted-foreground'>-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Source run info for latest execution */}
          {!isLoading && !error && executions.length > 0 && executions[0] && (
            <div className='mt-6 p-4 bg-muted/30 rounded-lg'>
              <h4 className='font-medium text-sm mb-2'>Latest Execution Details</h4>
              <div className='grid grid-cols-2 gap-2 text-sm'>
                <div>
                  <span className='text-muted-foreground'>Source Task:</span>{' '}
                  <span className='font-mono text-xs'>{executions[0].source_task_name}</span>
                </div>
                <div>
                  <span className='text-muted-foreground'>Apify Run:</span>{' '}
                  <span className='font-mono text-xs'>{executions[0].source_apify_run_id}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ExecutionHistoryDrawer;
